<script lang="ts">
  import { onDestroy } from 'svelte'
  import { debounce, orderBy } from 'es-toolkit'
  import { ThreeItemKeys, ThreeItemTypes } from '../../../../../constants/three'
  import useObjectQueries from '../../../hooks/useObjectQueries'
  import usePositioningAssistant from '../../../hooks/usePositioningAssistant'
  import FileInput from '../../../../../components/file-input/FileInput.svelte'
  import i18n from '../../../../../i18n'
  import SectionContainer from '../SectionContainer.svelte'
  import Button from '../../../../../components/button/Button.svelte'
  import { Crosshair, Info, X } from '@lucide/svelte'
  import Select from '../../../../../components/select/Select.svelte'
  import useObjectsQueries from '../../../hooks/useObjectsQueries'
  import { convertToSceneUnit } from '../../../../../helpers/scene-json'
  import PositioningAssistantHelp from '../../../../../components/positioning-assistant-help/PositioningAssistantHelp.svelte'
  import Tooltip from '../../../../../components/tooltip/Tooltip.svelte'
  import InputLabel from '../InputLabel.svelte'
  import ControlGroup from '../../../../../components/control-group/ControlGroup.svelte'
  import ControlGroupContent from '../../../../../components/control-group/ControlGroupContent.svelte'

  type Props = { objectId: string }
  const { objectId }: Props = $props()

  const { uploadPositionObjectMutation, checkPositionsComplianceMutation }
    = usePositioningAssistant()
  const { updateUserDataMutation, useObject } = useObjectQueries()
  const { useObjects } = useObjectsQueries()

  let isHelpOpen = $state(false)
  let selectedFileUuid = $state<string | null>(null)
  let icpCoordinates = $state<string>()
  let icpFileName = $state<string>()
  let isSyncingFromObject = false
  let objectQuery = $derived(useObject(objectId))
  let allObjects = $derived(useObjects())
  let object = $derived(objectQuery.data)
  let objectIsPrinter = $derived(
    object?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER,
  )

  const files = $derived(
    objectIsPrinter
      ? orderBy(
        (allObjects.data ?? []).filter(
          item =>
            item.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
              && item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID]
              === object?.userData[ThreeItemKeys.PRINTER_ID]
              && !!item.userData[ThreeItemKeys.ICP_COORDINATES],
        ),
        [item => item.userData[ThreeItemKeys.PRINTING_ORDER]],
        ['asc'],
      )
      : undefined,
  )

  const onStateUpdate = debounce(
    (targetObjectId: string, props: Record<string, string | undefined>) => {
      updateUserDataMutation.mutate({
        objectUuid: targetObjectId,
        object: props,
      })
    },
    500,
  )

  onDestroy(() => onStateUpdate.cancel())

  const onCoordinateFileUpload: HTMLInputElement['onchange'] = (event) => {
    uploadPositionObjectMutation.mutate(
      { event },
      {
        onSuccess: (coordinates) => {
          if (coordinates) {
            icpCoordinates = JSON.stringify(coordinates)
            icpFileName = (
              (event.target as HTMLInputElement).files as FileList
            )[0].name
          }
        },
      },
    )
  }

  const onClear = () => {
    icpCoordinates = undefined
    icpFileName = undefined
    onStateUpdate(objectId, {
      [ThreeItemKeys.ICP_COORDINATES]: undefined,
      [ThreeItemKeys.ICP_FILE_NAME]: undefined,
    })
  }

  const runIcp = () => {
    if (!objectId || !selectedFileUuid) {
      return
    }

    checkPositionsComplianceMutation.mutate(
      { printerUuid: objectId, fileUuid: selectedFileUuid },
      {
        onSuccess: (result) => {
          if (result) {
            object?.rotation.set(0, 0, result.rotation * (Math.PI / 180))
            object?.position.setX(
              convertToSceneUnit(result.translation.x) as number,
            )
            object?.position.setY(
              convertToSceneUnit(result.translation.y) as number,
            )
          }
        },
      },
    )
  }

  $effect(() => {
    isSyncingFromObject = true
    icpCoordinates = object?.userData[ThreeItemKeys.ICP_COORDINATES]
    icpFileName = object?.userData[ThreeItemKeys.ICP_FILE_NAME]
  })

  $effect(() => {
    const bothDefined
      = icpCoordinates !== undefined && icpFileName !== undefined
    const props = {
      [ThreeItemKeys.ICP_COORDINATES]: icpCoordinates,
      [ThreeItemKeys.ICP_FILE_NAME]: icpFileName,
    }

    if (isSyncingFromObject) {
      isSyncingFromObject = false
      return
    }

    if (bothDefined) {
      onStateUpdate(objectId, props)
    }
  })
</script>

<SectionContainer
  buttonIcon={Info}
  buttonOnClick={() => {
    isHelpOpen = true
  }}
  buttonTooltip={$i18n.t('globals.help')}
  title={$i18n.t('project.rightBar.positioningAssistant.title')}
>
  {#if icpCoordinates && icpFileName}
    <div class="existing-file-container">
      <p>{icpFileName}</p>
      <Button onclick={onClear} size="xs" variant="ghost"><X /></Button>
    </div>
    {#if object?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER}
      <div class="positioning-form">
        <Select
          name="file"
          options={(files ?? []).map(file => ({
            value: file.uuid,
            label: file.name,
          }))}
          placeholder={$i18n.t(
            'project.rightBar.positioningAssistant.selectFile',
          )}
          bind:value={selectedFileUuid}
        />
        <Tooltip title={$i18n.t('project.rightBar.positioningAssistant.run')}>
          <Button
            disabled={!selectedFileUuid}
            onclick={runIcp}
            size="sm"
            variant="secondary"
          >
            <Crosshair />
          </Button>
        </Tooltip>
      </div>
    {/if}
  {:else}
    <ControlGroup>
      <InputLabel
      >{$i18n.t(
        'project.rightBar.positioningAssistant.inputLabel',
      )}</InputLabel
      >
      <ControlGroupContent>
        <FileInput
          name="file-coordinate"
          accept=".csv, .dxf"
          onchange={onCoordinateFileUpload}
          placeholder={$i18n.t(
            'project.rightBar.positioningAssistant.inputPlaceholder',
          )}
          sizeVariant="sm"
        />
      </ControlGroupContent>
    </ControlGroup>
  {/if}
</SectionContainer>

<PositioningAssistantHelp bind:isOpen={isHelpOpen} />

<style lang="scss">
  p {
    color: var(--right-bar-muted-foreground);
    font-size: 0.75rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .existing-file-container {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: space-between;
    overflow: hidden;
  }

  .positioning-form {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 0.25rem;
  }
</style>
